import clsx from 'clsx';
import moment from 'moment-timezone';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import promptImage from 'resources/img/hs-prompt-image-2.jpg';
import { openAIAssistantWithExternalPrompt } from '@/aiAssistant/aiAssistant.actions';
import { useAiAssistantDisplayed } from '@/aiAssistant/aiAssistant.store';
import { ContainerWithHTML } from '@/core/ContainerWithHTML.atom';
import { sqHomeScreenStore, sqWorkbenchStore } from '@/core/core.stores';
import { useFluxPath } from '@/core/hooks/useFluxPath.hook';
import { CopyBranchCode } from '@/homescreen/CopyBranchCode.atom';
import { setTrainingPromptDisplay } from '@/homescreen/homescreen.actions';
import { TRAINING_PROMPT_ID, TrainingPromptDisplay } from '@/homescreen/homescreen.constants';
import { genAIEnabled, isLearnModeEnabled, isNewHomeScreen2025Enabled } from '@/services/systemConfiguration.utilities';
import { TRACK } from '@/track/track.constants';
import { doTrack } from '@/track/track.service';
import { GET_STARTED_WITH_SEEQ_LINK, TRAINING_TELEMETRY_HOST } from '@/utilities/externalLinks';
import { infoToast, warnToast } from '@/utilities/toast.utilities';
import { faClose, faSparkles } from '@seeqdev/icons/fa-sharp-regular-svg-icons';
import { Button, ExternalLink, Icon } from '@seeqdev/qomponents';

/**
 * This component renders content of the Training "Get Started" prompt for the Home Screen Page.
 * It DOES NOT include the outer box with the dismiss button.
 */
export const TrainingPromptContent = ({ isModal, onClose }: { isModal?: boolean; onClose?: () => void }) => {
  const { t } = useTranslation();
  const isTrusted = useFluxPath(sqWorkbenchStore, () => sqWorkbenchStore.currentUser.isTrusted);
  const userFirstName = useFluxPath(sqWorkbenchStore, () => sqWorkbenchStore.currentUser.firstName);
  const isAiAssistantDisplayed = useAiAssistantDisplayed();
  const branchCode = useFluxPath(sqHomeScreenStore, () => sqHomeScreenStore.branchCode);
  const hasBranchCode = Boolean(branchCode?.trim());
  const firstLegacyStep = hasBranchCode
    ? t('HOME_SCREEN.TRAINING.STEP_1', { branchCode: `<b>${branchCode}</b>` })
    : t('HOME_SCREEN.TRAINING.STEP_1_NO_CODE', {
        email: '<a href="mailto:training@seeq.com">training@seeq.com</a>',
      });
  const steps = [
    firstLegacyStep,
    t('HOME_SCREEN.TRAINING.STEP_2', {
      website: `<a href="${TRAINING_TELEMETRY_HOST.href}">${TRAINING_TELEMETRY_HOST.label}</a>`,
    }),
    t('HOME_SCREEN.TRAINING.STEP_3'),
  ];
  const [videoClickCount, setVideoClickCount] = useState(0);
  const [videoStartTime, setVideoStartTime] = useState(moment.now());

  const isNewHomeScreenEnabled = isNewHomeScreen2025Enabled();

  useEffect(() => {
    doTrack(TRACK.TRAINING, 'home screen intro video shown');
  }, []);

  const renderPromptHeader = (header: string, subheader: string) => (
    <div className="tw:mb-sq-15 tw:flex tw:flex-col tw:self-start">
      <h4
        className={clsx('text-bolder tw:mt-0!', {
          'tw:text-sq-homescreen-section-title': isNewHomeScreenEnabled,
          'homescreen-title-text tw:dark:text-white': !isNewHomeScreenEnabled,
        })}>
        {header}
      </h4>
      <span>{subheader}</span>
    </div>
  );

  const renderPromptFooter = (header: React.ReactNode, subheader?: string) => (
    <div className="tw:flex tw:flex-col">
      <span>{header}</span>
      {subheader && <span>{subheader}</span>}
    </div>
  );

  const renderNewPromptContent = () => (
    <div className="tw:flex tw:w-full tw:flex-col" data-testid="trainingPromptContentNew">
      <div className={clsx('tw:flex tw:w-full tw:gap-7.5', isModal ? 'tw:flex-col' : 'tw:flex-row')}>
        <div className="homeScreenSectionContainer tw:flex tw:w-1/2 tw:flex-col tw:!bg-sq-homescreen-gray-bg tw:dark:!border-none tw:dark:!bg-transparent tw:dark:!p-0">
          {renderPromptHeader(t('HOME_SCREEN.TRAINING.HANDS_ON_TRAINING'), t('HOME_SCREEN.TRAINING.REAL_USE_CASES'))}
          <ExternalLink
            href={TRAINING_TELEMETRY_HOST.href}
            extraClassNames="tw:h-[stretch] tw:max-h-[30vh] tw:block tw:rounded-md tw:relative tw:overflow-hidden tw:no-underline tw:h-full tw:w-full">
            <img
              alt={t('HOME_SCREEN.TRAINING.PROMPT_IMAGE_ALT')}
              className="tw:absolute tw:inset-0 tw:bg-cover tw:bg-center"
              src={promptImage}
            />
          </ExternalLink>
          {renderPromptFooter(
            <>
              <ol className="tw:mb-0! tw:pl-3">
                {!isLearnModeEnabled() &&
                  (hasBranchCode ? (
                    <li>
                      <CopyBranchCode text="HOME_SCREEN.TRAINING.COPY_BRANCH_CODE" branchCode={branchCode!} />
                    </li>
                  ) : (
                    <li>{t('HOME_SCREEN.TRAINING.NO_BRANCH_CODE')}</li>
                  ))}
                <li>
                  <ExternalLink
                    label={
                      isLearnModeEnabled() ? t('HOME_SCREEN.TRAINING.GO_TO') : t('HOME_SCREEN.TRAINING.REGISTER_ON')
                    }
                    href={TRAINING_TELEMETRY_HOST.href}>
                    {TRAINING_TELEMETRY_HOST.label}
                  </ExternalLink>
                </li>
              </ol>
            </>,
          )}
        </div>
        <div className="homeScreenSectionContainer tw:flex tw:w-1/2 tw:flex-col tw:!bg-sq-homescreen-gray-bg tw:dark:!border-none tw:dark:!bg-transparent tw:dark:!p-0">
          {renderPromptHeader(t('HOME_SCREEN.TRAINING.WHAT_IS_SEEQ'), t('HOME_SCREEN.TRAINING.EXPLORE'))}
          <video
            className="tw:mb-sq-10 tw:h-full tw:w-full tw:rounded-md"
            controls={true}
            autoPlay={false}
            disablePictureInPicture={true}
            controlsList="nodownload"
            onClick={() => {
              if (videoClickCount % 2) {
                const endTime = moment.now();
                const duration = endTime - videoStartTime;
                doTrack(TRACK.TRAINING, 'Homescreen video played v2 for', moment(duration).format('mm:ss'));
              }

              if (!videoClickCount) {
                doTrack(TRACK.TRAINING, 'Homescreen video played v2');
              }
              setVideoClickCount(videoClickCount + 1);
              setVideoStartTime(moment.now());
            }}>
            <source src="https://telemetry.seeq.com/seeq-intro-video" />
          </video>
          {renderPromptFooter(
            <div className="tw:italic">
              {t('VIDEO.TROUBLE_VIEWING')}
              <ExternalLink href="https://www.seeq.com/product/our-offerings/" extraClassNames="tw:ml-sq-5">
                {t('VIDEO.WATCH_HERE')}
              </ExternalLink>
            </div>,
          )}
        </div>
      </div>
    </div>
  );

  const renderLegacyPromptContent = () => (
    <div
      className={clsx('tw:flex tw:w-full tw:flex-col', {
        'tw:items-center tw:justify-center': !isNewHomeScreenEnabled,
      })}
      data-testid="trainingPromptContent">
      <>
        <h1 className="text-bolder">{t('HOME_SCREEN.WELCOME')}</h1>
        <div className="trainingPromptText tw:mr-5 tw:ml-5 tw:text-center">
          {t('HOME_SCREEN.NEW_USER_MESSAGE', { name: userFirstName })}
        </div>
      </>
      <div
        className={clsx(
          'tw:mt-3 tw:flex tw:w-full tw:justify-center tw:gap-1 tw:border-t tw:border-t-sq-disabled-gray tw:pt-3',
          isModal ? 'tw:flex-col' : 'tw:flex-row',
        )}>
        <div
          className={clsx('tw:flex tw:flex-col tw:items-center tw:p-3', {
            homeScreenVideo: !isModal,
          })}>
          <video
            className={clsx('tw:mb-sq-10 tw:h-full tw:w-full tw:rounded-md', {
              'tw:max-w-[35vh]': !isModal,
            })}
            controls={true}
            autoPlay={false}
            disablePictureInPicture={true}
            controlsList="nodownload"
            onClick={() => {
              if (videoClickCount % 2) {
                const endTime = moment.now();
                const duration = endTime - videoStartTime;
                doTrack(TRACK.TRAINING, 'Homescreen video played v2 for', moment(duration).format('mm:ss'));
              }

              if (!videoClickCount) {
                doTrack(TRACK.TRAINING, 'Homescreen video played v2');
              }
              setVideoClickCount(videoClickCount + 1);
              setVideoStartTime(moment.now());
            }}>
            <source src="https://telemetry.seeq.com/seeq-intro-video" />
          </video>
          {renderPromptFooter(
            <div className={clsx('xsmall tw:mt-sq-5 tw:italic')}>
              {t('VIDEO.TROUBLE_VIEWING')}
              <ExternalLink href={GET_STARTED_WITH_SEEQ_LINK} extraClassNames="tw:ml-sq-5">
                {t('VIDEO.WATCH_HERE')}
              </ExternalLink>
            </div>,
          )}
        </div>
        <div className="tw:flex tw:flex-col tw:justify-center">
          {genAIEnabled() && isTrusted && (
            <div className="tw:mb-sq-10 tw:flex tw:flex-col tw:items-center">
              <Button
                label={t('AI_ASSISTANT.GET_STARTED')}
                icon={faSparkles}
                variant="theme"
                onClick={() => {
                  doTrack(TRACK.AI_ASSISTANT, 'get started prompt clicked');
                  openAIAssistantWithExternalPrompt(
                    { prompt: t('AI_ASSISTANT.GET_STARTED_PROMPT'), agent: 'general' },
                    { wasDisplayed: isAiAssistantDisplayed },
                  );
                  isModal && onClose && onClose();
                }}
              />
            </div>
          )}
          <div className="trainingPrompt">
            {hasBranchCode ? t('HOME_SCREEN.TRAINING.NEXT_BRANCH_PROMPT') : t('HOME_SCREEN.TRAINING.NEXT_PROMPT')}
          </div>
          {hasBranchCode && (
            <div className="tw:mt-sq-10 tw:font-bold">{t('HOME_SCREEN.TRAINING.NEXT_BRANCH_SUBTITLE')}</div>
          )}
          <ol className="trainingPromptText">
            {steps.map((step: string, idx: number) => (
              <li key={idx}>
                <ContainerWithHTML content={step} />
              </li>
            ))}
          </ol>
        </div>
      </div>
    </div>
  );

  return isNewHomeScreenEnabled ? renderNewPromptContent() : renderLegacyPromptContent();
};

/**
 * This component renders the Training "Get Started" prompt for the Home Screen Page.
 * It includes the outer box with the dismiss button.
 */
export const TrainingPrompt = () => {
  const isNewHomeScreenEnabled = isNewHomeScreen2025Enabled();
  const { t } = useTranslation();

  const renderLegacyPanel = () => (
    <div className="tw:relative tw:mr-5 tw:mb-5 tw:ml-5 tw:flex tw:flex-col tw:items-center tw:rounded-md! tw:border tw:border-sq-disabled-gray">
      <div className="tw:absolute tw:top-sq-10 tw:right-sq-10 tw:flex tw:flex-row">
        <span className="tw:mr-sq-5">{t('HOME_SCREEN.TRAINING.HIDE_THIS_PANEL')}</span>
        <Icon
          onClick={() => {
            setTrainingPromptDisplay(TrainingPromptDisplay.NONE);
            doTrack(TRACK.TRAINING, 'get started prompt closed');
            warnToast({ messageKey: 'HOME_SCREEN.RESUME_TRAINING' });
          }}
          icon={faClose}
          extraClassNames="fa-xlg hoverGray tw:cursor-pointer"
          testId="trainingDismissIcon"
          type={isNewHomeScreenEnabled ? undefined : 'gray'}
        />
      </div>
      <TrainingPromptContent />
    </div>
  );

  const dismissTraining = () => {
    setTrainingPromptDisplay(TrainingPromptDisplay.NONE);
    doTrack(TRACK.TRAINING, 'get started prompt closed');
    infoToast({ messageKey: 'HOME_SCREEN.TRAINING.DISMISS_TRAINING_TEXT' });
  };

  const renderNewPanel = () => (
    <div id={TRAINING_PROMPT_ID} className="homeScreenSectionContainer tw:flex tw:flex-col tw:dark:!border-none">
      <div className="tw:-mt-sq-10 tw:mb-sq-10 tw:flex tw:flex-row tw:items-end tw:justify-between">
        <h3 className="tw:m-0!">{t('HOME_SCREEN.TRAINING.TRAINING_TITLE')}</h3>
        <div className="tw:flex tw:flex-row tw:items-center">
          <span className="homescreen-action tw:mr-sq-5 tw:cursor-pointer tw:text-sq-15" onClick={dismissTraining}>
            {t('HOME_SCREEN.TRAINING.HIDE_WINDOW')}
          </span>
          <Icon
            onClick={dismissTraining}
            icon={faClose}
            extraClassNames="fa-xlg homescreen-action hoverGray tw:cursor-pointer"
            testId="trainingDismissIcon"
            type={isNewHomeScreenEnabled ? undefined : 'gray'}
          />
        </div>
      </div>
      <TrainingPromptContent />
    </div>
  );
  return isNewHomeScreenEnabled ? renderNewPanel() : renderLegacyPanel();
};
